import { isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { pushIfNotExists } from "@fidget/common/utils/arrayHelpers";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { SettingsForm } from "../settingsForm";
import { DeleteConfirmationToastOnError } from "../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { ElementFormContent } from "../settingsForms/elementForm/content/elementFormContent";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormSaveDiscardIcon } from "../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { ListFilterWrapper } from "../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { AdviserFilter } from "../settingsForms/filters/pickerFilters/adviserFilter";
import { TagFilter } from "../settingsForms/filters/pickerFilters/tagFilter";
import { TagGroupFilter } from "../settingsForms/filters/pickerFilters/tagGroupFilter";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import {
  ListDeleteAction,
  ListActions,
  ListActionsSection,
  ListTagTagGroupAction,
  ListAdviserAction
} from "../listActions";
import { VerticalSeparator } from "../settingsForms/separators/verticalSeparator/verticalSeparator";
import { ExistingAgentGroupBox } from "./components/existingAgentGroupBox/existingAgentGroupBox";
import { NewAgentGroupBox } from "./components/newAgentGroupBox/newAgentGroupBox";
import { ExistingAgentGroupForm } from "./types";
import { useAgentGroupsSettings } from "./useAgentGroupsSettings";
import { useStore } from "../../../models";

export const AgentGroupSettingsList = observer(() => {
  const {
    newItem,
    handleCreateNewClick,
    saveNewItem,
    discardNewItem,

    items,
    selectedItems,
    setItemsSelected,
    filteredItems,
    setItemSelected,
    deleteAllSelected,
    forEachSelected,
    uiRemoveExistingItem,
    apiDeleteItem,

    editedItems,
    onExistingEditClick,
    onSubmitAll,
    onDiscardAll
  } = useAgentGroupsSettings();
  const { uiStore } = useStore();

  const renderNewItemSection = () => {
    return newItem ? (
      <ElementForm isEdited>
        <ElementFormContent>
          <NewAgentGroupBox form={newItem} />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </ElementForm>
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={"+ " + intl.formatMessage("settings.adviserGroups.addNew")}
      />
    );
  };

  const renderElementForm = (group: ExistingAgentGroupForm, onDeleteClick: () => void) => {
    return (
      <ElementForm>
        <ElementFormActions>
          <ElementFormCheckbox
            isSelected={!!selectedItems[group.$.id.$]}
            onChange={(val) => setItemSelected(group, val)}
            id={group.$.id.$}
          />
        </ElementFormActions>
        <VerticalSeparator />
        <ElementFormContent>
          <ExistingAgentGroupBox form={group} />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormEditIcon onClick={() => onExistingEditClick(group)} />
          {uiStore.canDisplayAgentGroupsConfiguration() && (
            <ElementFormDeleteIcon onClick={onDeleteClick} />
          )}
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderAdviserGroups = () => (
    <div>
      {filteredItems.map((group) => {
        const groupId = group.$.id.$;

        return (
          <DeleteConfirmationToastOnError
            key={groupId}
            areYouSureText={intl.formatMessage("settings.adviserGroups.areYouSureToRemove")}
            succesfullyDeletedText={intl.formatMessage(
              "settings.adviserGroups.removedSuccessfully"
            )}
            onTimeoutPassed={() => uiRemoveExistingItem(groupId)}
            onDelete={() => apiDeleteItem(groupId)}
            renderComponent={(onDeleteClick) => renderElementForm(group, onDeleteClick)}
          />
        );
      })}
    </div>
  );

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingAgentGroupForm>>
        <ListFilterWrapper<ExistingAgentGroupForm>
          placeholder={intl.formatMessage("settings.adviserGroups.filters.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="AdviserGroupsInputFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.name.$, input)}
          headerText={intl.formatMessage("settings.adviserGroups")}
        >
          <TagFilter<ExistingAgentGroupForm>
            predicate={(tag) => (item) => item.$.tagIds.$.includes(tag.id)}
          />
          <TagGroupFilter<ExistingAgentGroupForm>
            predicate={(tagGroup) => (item) => item.$.tagGroupIds.$.includes(tagGroup.id)}
          />
          <AdviserFilter<ExistingAgentGroupForm>
            predicate={(adviser) => (item) => item.$.adviserIds.$.includes(adviser.id)}
          />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected = !!filteredItems.length && size(selectedItems) === size(filteredItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) => setItemsSelected(filteredItems, val)}
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            {uiStore.canDisplayAgentGroupsConfiguration() && (
              <>
                <ListAdviserAction
                  text={intl.formatMessage("settings.adviserGroups.actions.addAdviser")}
                  onPicked={(adviserId) =>
                    forEachSelected((i) =>
                      i.$.adviserIds.onChange(pushIfNotExists(i.$.adviserIds.$, adviserId))
                    )
                  }
                />
                <ListAdviserAction
                  text={intl.formatMessage("settings.adviserGroups.actions.removeAdviser")}
                  onPicked={(adviserId) =>
                    forEachSelected((i) =>
                      i.$.adviserIds.onChange(i.$.adviserIds.$.filter((id) => id !== adviserId))
                    )
                  }
                />
              </>
            )}
            {uiStore.canDisplayAdvisersAssignments() && (
              <>
                <ListTagTagGroupAction
                  textVariant="adding"
                  text={intl.formatMessage("settings.advisers.action.addSkill")}
                  onTagGroupPicked={(groupId) =>
                    forEachSelected((i) =>
                      i.$.tagGroupIds.onChange(pushIfNotExists(i.$.tagGroupIds.$, groupId))
                    )
                  }
                  onTagPicked={(tagId) =>
                    forEachSelected((i) =>
                      i.$.tagIds.onChange(pushIfNotExists(i.$.tagIds.$, tagId))
                    )
                  }
                />
                <ListTagTagGroupAction
                  textVariant="removing"
                  text={intl.formatMessage("settings.advisers.action.removeSkill")}
                  onTagGroupPicked={(groupId) =>
                    forEachSelected((i) =>
                      i.$.tagGroupIds.onChange(i.$.tagGroupIds.$.filter((v) => groupId !== v))
                    )
                  }
                  onTagPicked={(tagId) =>
                    forEachSelected((i) =>
                      i.$.tagIds.onChange(i.$.tagIds.$.filter((v) => tagId !== v))
                    )
                  }
                />
              </>
            )}
          </ListActionsSection>
          {uiStore.canDisplayAgentGroupsConfiguration() && (
            <>
              <VerticalSeparator />
              <ListDeleteAction onDelete={deleteAllSelected} />
            </>
          )}
        </ListActions>
      </SettingsForm>
    );
  };

  return (
    <>
      {uiStore.canDisplayAgentGroupsConfiguration() && renderNewItemSection()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderAdviserGroups()}
    </>
  );
});
